/**
 * Subscription/billing history (prototype, GDN-2579): the org's SaaS-billing
 * event timeline from the analytics store — Stripe (not Stripe Connect),
 * bundle assignments, payment methods, admin credits/discounts/refunds.
 * Rows expand to the raw event payload.
 *
 * Degrades to nothing when the backend has no analytics store configured.
 */
import { Fragment, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { OrgActivityEntry } from '../store/api';
import { useGetOrganizationActivityQuery } from '../store/hooks';
import { CardTitle } from './card-title';
import { EventActor } from './event-actor';

const formatDateTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const centsToDollars = (cents: unknown): string =>
  typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '';

/** Human one-liner per event type, built from payload fields. */
const summarize = (entry: OrgActivityEntry): string => {
  const d = (entry.data ?? {}) as Record<string, unknown>;
  switch (entry.event_type) {
    case 'SUBSCRIPTION_CREATED':
      return 'Subscription created';
    case 'SUBSCRIPTION_STATUS_CHANGED':
      return `${d.previous_status ?? '?'} → ${d.new_status ?? '?'}`;
    case 'BUNDLE_ASSIGNED':
      return `${d.bundle_name ?? d.bundle_id} (${d.bundle_type ?? 'bundle'})${
        d.target_user_id ? ' → user' : d.is_org_level ? ' (org-wide)' : ''
      }`;
    case 'BUNDLE_UNASSIGNED':
      return `${d.bundle_name ?? d.bundle_id} removed`;
    case 'BUNDLE_STATUS_CHANGED':
      return `${d.bundle_name ?? d.bundle_id ?? 'bundle'}: ${
        d.previous_status ?? '?'
      } → ${d.new_status ?? '?'}`;
    case 'PAYMENT_METHOD_UPDATED':
      return `${d.card_brand ?? ''} ${d.payment_method_type ?? 'payment method'}${
        d.subscription_resumed ? ' (subscription resumed)' : ''
      }`.trim();
    case 'BILLING_INTERVAL_CHANGED':
      return `${d.previous_interval ?? '?'} → ${d.new_interval ?? '?'}`;
    case 'BUNDLES_SYNCED_TO_STRIPE':
      return 'Bundles synced to Stripe';
    case 'ADMIN_ORGANIZATION_CREDIT_APPLIED':
      return `Credit ${centsToDollars(d.amount)} — ${d.reason ?? 'no reason given'}`;
    case 'ADMIN_ORGANIZATION_REFUND_ISSUED':
      return `Refund ${centsToDollars(d.amount)} — ${d.reason ?? 'no reason given'}`;
    case 'ADMIN_ORGANIZATION_DISCOUNT_APPLIED':
      return `Discount applied${d.reason ? ` — ${d.reason}` : ''}`;
    case 'ADMIN_ORGANIZATION_DISCOUNT_REMOVED':
      return 'Discount removed';
    case 'ADMIN_ORGANIZATION_DISCOUNT_RELABELLED':
      return 'Discount relabelled';
    default:
      return '';
  }
};

const HistoryRow = ({ entry }: { entry: OrgActivityEntry }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <TableRow hover onClick={() => setOpen(previous => !previous)} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox">
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {formatDateTime(entry.occurred_at)}
        </TableCell>
        <TableCell>
          <Chip size="xSmall" variant="soft" color="info" label={entry.event_type} />
        </TableCell>
        <TableCell>{summarize(entry)}</TableCell>
        <TableCell>
          <EventActor actor={entry.actor} onBehalfOf={entry.on_behalf_of} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          {open && (
            <Box sx={{ py: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: 'monospace', mb: 1 }}
              >
                event_id: {entry.event_id}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: 12,
                  overflowX: 'auto',
                }}
              >
                {JSON.stringify(entry.data ?? {}, null, 2)}
              </Box>
            </Box>
          )}
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export const OrganizationBillingHistoryCard = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const { data, isLoading, isFetching, error, refetch } =
    useGetOrganizationActivityQuery({
      organizationId,
      family: 'billing',
    });

  const errorCode =
    error && 'data' in error
      ? (error.data as { error_code?: string } | undefined)?.error_code
      : undefined;
  if (errorCode === 'activity_store_not_configured') return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        disableTypography
        title={<CardTitle>Subscription history</CardTitle>}
        action={
          <IconButton
            aria-label="Reload subscription history"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? <CircularProgress size={18} /> : <RefreshIcon />}
          </IconButton>
        }
      />
      <CardContent>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && error && (
          <Alert severity="warning">
            The analytics store did not answer; history is temporarily
            unavailable.
          </Alert>
        )}
        {!isLoading && !error && !data?.entries?.length && (
          <Typography color="text.secondary">
            No recorded billing events for this organization.
          </Typography>
        )}
        {!isLoading && !error && !!data?.entries?.length && (
          <Table size="small" aria-label="Subscription history">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell>Actor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.entries.map(entry => (
                <HistoryRow key={entry.event_id} entry={entry} />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
